import { DirectiveOptions } from 'vue';

/**
 * ARIA combobox semantics for `vue-multiselect`.
 *
 * The library renders `role="combobox"` on its wrapper <div> while focus lives on
 * the inner search <input>. ARIA requires the state to sit on the focused element,
 * so a screen reader never reads state left on the wrapper. On top of that the
 * library renders no `aria-expanded` and no `aria-activedescendant` at all, and
 * with no `id` prop its `aria-owns="listbox-null"` points at nothing.
 *
 * This directive moves the combobox onto the element that actually takes focus and
 * keeps its state in sync with the library's own `isOpen` / `pointer`, so there is
 * no second copy of the truth. What the library already gets right is reused:
 *
 *   <ul role="listbox" id="listbox-{id}">
 *     <li role="option">
 *
 * The option ids the library generates are positional (`{id}-{index}`), so the
 * same id means a different country as soon as the list is filtered. They are
 * rewritten here to the option's own identity taken from `track-by`, which is
 * what the list is keyed on anyway: `citizenship-option-at` instead of
 * `citizenship-3`.
 *
 * Usage: `<multiselect v-a11y-combobox id="citizenship" … />` - the `id` is
 * required, every reference above is built from it.
 */

type Teardown = () => void;

type Multiselect = {
  id: string | null
  isOpen: boolean
  pointer: number
  trackBy?: string
  filteredOptions: unknown[]
  $on: (event: string, cb: () => void) => void
  $off: (event: string, cb: () => void) => void
}

const teardowns = new WeakMap<HTMLElement, Teardown>();

function slug(value: unknown): string {
  if (value === null || value === undefined || value === '') return '';
  return String(value)
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

/** The option's own identity, falling back to its position when it has none */
function optionKey(ms: Multiselect, option: unknown, index: number): string {
  if (option !== null && typeof option === 'object') {
    // Group headers carry no track-by value, only a label
    const value = ms.trackBy ? (option as Record<string, unknown>)[ms.trackBy] : null;
    return slug(value) || String(index);
  }
  return slug(option) || String(index);
}

/**
 * Replaces the library's positional option ids with identity-based ones and
 * returns the elements in `filteredOptions` order - only the <li> of the
 * v-for carry `multiselect__element`, so the two line up index by index.
 */
function syncOptionIds(el: HTMLElement, ms: Multiselect): HTMLElement[] | null {
  const items = Array.from(el.querySelectorAll<HTMLElement>('li.multiselect__element'));
  // The pointer watcher fires before Vue patches the list, so the DOM can still
  // hold the previous options. Labelling those would hand out positional ids for
  // rows that are about to be dropped - componentUpdated redoes this in order.
  if (items.length !== ms.filteredOptions.length) return null;

  const taken = new Set<string>();

  items.forEach((item, index) => {
    const key = optionKey(ms, ms.filteredOptions[index], index);
    // track-by is meant to be unique; the index keeps us honest if it is not
    const id = taken.has(`${ms.id}-option-${key}`)
      ? `${ms.id}-option-${key}-${index}`
      : `${ms.id}-option-${key}`;
    taken.add(id);
    if (item.id !== id) item.setAttribute('id', id);
  });

  return items;
}

function sync(el: HTMLElement, ms: Multiselect) {
  const listboxId = `listbox-${ms.id}`;
  const input = el.querySelector<HTMLElement>('input.multiselect__input');
  // Without a search input the wrapper itself is focusable and stays the combobox
  const combobox = input || el;

  if (combobox !== el) {
    // Two comboboxes for one control would be worse than none
    el.setAttribute('role', 'presentation');
    el.removeAttribute('aria-owns');
  }

  combobox.setAttribute('role', 'combobox');
  combobox.setAttribute('aria-haspopup', 'listbox');
  combobox.setAttribute('aria-autocomplete', 'list');
  combobox.setAttribute('aria-controls', listboxId);
  combobox.setAttribute('aria-expanded', ms.isOpen ? 'true' : 'false');

  const options = syncOptionIds(el, ms);
  if (!options) return;

  const active = ms.isOpen && ms.pointer >= 0 ? options[ms.pointer] : null;
  // The pointer can also sit on a group header, which is not a selectable option
  if (active && active.getAttribute('role') === 'option') {
    combobox.setAttribute('aria-activedescendant', active.id);
  } else {
    combobox.removeAttribute('aria-activedescendant');
  }
}

function instance(vnode: { componentInstance?: unknown }): Multiselect | null {
  const ms = vnode.componentInstance as Multiselect | undefined;
  if (!ms || ms.id === null || ms.id === undefined) return null;
  return ms;
}

const a11yCombobox: DirectiveOptions = {
  inserted(el, binding, vnode) {
    const ms = instance(vnode);
    if (!ms) {
      // eslint-disable-next-line no-console
      console.warn('v-a11y-combobox: <multiselect> needs an id, ARIA references cannot be built without one');
      return;
    }

    // The directive's own componentUpdated only fires when the PARENT re-renders,
    // but the multiselect re-renders itself while typing. Its own updated hook is
    // the only signal that runs after every list patch - and since opening,
    // closing and moving the pointer all re-render, it covers the whole state.
    const onUpdated = () => sync(el, ms);
    ms.$on('hook:updated', onUpdated);
    teardowns.set(el, () => ms.$off('hook:updated', onUpdated));

    sync(el, ms);
  },

  componentUpdated(el, binding, vnode) {
    const ms = instance(vnode);
    if (ms) sync(el, ms);
  },

  unbind(el) {
    const teardown = teardowns.get(el);
    if (teardown) teardown();
    teardowns.delete(el);
  },
};

export default a11yCombobox;
